import { useEffect, useMemo, useState } from "react";
import { api, currency, fmtDate } from "@/lib/api";
import { PRESETS, getRange } from "@/lib/dates";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Search, Receipt, Pencil, PoundSterling, Percent, Hash } from "lucide-react";
import { toast } from "sonner";
import ConfirmDialog from "@/components/ConfirmDialog";

const today = () => new Date().toISOString().slice(0, 10);
const JOINT = "__joint__";
const empty = { date: today(), category: "", supplier: "", amount_excl: "", vat_amount: "", staff: JOINT, notes: "", include_in_pl: true };

export default function Expenses() {
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [staff, setStaff] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [query, setQuery] = useState("");
  const [preset, setPreset] = useState("all");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");

  const load = async () => {
    const { data } = await api.get("/expenses");
    setExpenses(data);
  };
  useEffect(() => {
    load();
    api.get("/settings").then(({ data }) => {
      setCategories(data.expense_categories || []);
      setStaff(data.staff || []);
    });
  }, []);

  const save = async () => {
    if (!form.date) return toast.error("Date is required");
    setSaving(true);
    try {
      const isJoint = form.staff === JOINT;
      const member = staff.find((m) => m.id === form.staff);
      const payload = {
        date: form.date,
        category: form.category || (categories[0] || "Other"),
        supplier: form.supplier,
        amount_excl: parseFloat(form.amount_excl) || 0,
        vat_amount: parseFloat(form.vat_amount) || 0,
        staff_id: isJoint ? null : form.staff,
        staff_name: isJoint ? "" : member ? member.name : "",
        joint: isJoint,
        notes: form.notes,
      };
      if (editingId) {
        await api.put(`/expenses/${editingId}`, payload);
        toast.success("Expense updated");
      } else {
        await api.post("/expenses", payload);
        toast.success("Expense added");
      }
      setForm(empty);
      setEditingId(null);
      setOpen(false);
      load();
    } catch {
      toast.error("Failed to save expense");
    } finally {
      setSaving(false);
    }
  };

  const openNew = () => {
    setEditingId(null);
    setForm({ ...empty, category: categories[0] || "" });
    setOpen(true);
  };

  const openEdit = (e) => {
    setEditingId(e.id);
    setForm({
      date: e.date,
      category: e.category || "",
      supplier: e.supplier || "",
      amount_excl: e.amount_excl ?? "",
      vat_amount: e.vat_amount ?? "",
      staff: e.joint || !e.staff_id ? JOINT : e.staff_id,
      notes: e.notes || "",
      include_in_pl: e.include_in_pl !== false,
    });
    setOpen(true);
  };

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const remove = (id, desc) => setDeleteTarget({ id, desc });

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/expenses/${deleteTarget.id}`);
      toast.success("Expense deleted");
      setDeleteTarget(null);
      load();
    } catch { toast.error("Failed to delete expense"); }
    finally { setDeleting(false); }
  };

  // Date range for the insight header
  const range = useMemo(() => {
    if (preset === "custom") return { start: customStart || null, end: customEnd || null };
    return getRange(preset);
  }, [preset, customStart, customEnd]);

  const inRange = (d) => {
    const x = (d || "").slice(0, 10);
    if (range.start && x < range.start) return false;
    if (range.end && x > range.end) return false;
    return true;
  };

  const rangeExpenses = useMemo(() => expenses.filter((e) => inRange(e.date)), [expenses, range]);

  const insight = useMemo(() => {
    const ex = rangeExpenses.reduce((s, e) => s + (e.amount_excl || 0), 0);
    const vat = rangeExpenses.reduce((s, e) => s + (e.vat_amount || 0), 0);
    const total = rangeExpenses.reduce((s, e) => s + (e.total || 0), 0);
    const r = (n) => Math.round(n * 100) / 100;
    return { ex: r(ex), vat: r(vat), total: r(total), count: rangeExpenses.length };
  }, [rangeExpenses]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = rangeExpenses;
    if (!q) return base;
    return base.filter(
      (e) =>
        (e.category || "").toLowerCase().includes(q) ||
        (e.supplier || "").toLowerCase().includes(q) ||
        (e.staff_name || "").toLowerCase().includes(q) ||
        (e.notes || "").toLowerCase().includes(q)
    );
  }, [rangeExpenses, query]);

  const Stat = ({ label, value, icon: Icon, accent, testid, sub }) => (
    <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs text-[#5B5C5F] uppercase tracking-wider">{label}</span>
        <Icon className="w-4 h-4" style={{ color: accent }} />
      </div>
      <p className="font-heading text-2xl font-bold mt-1" style={{ color: accent }}>
        {value}
      </p>
      {sub && <p className="text-xs text-[#5B5C5F] mt-0.5">{sub}</p>}
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto" data-testid="expenses-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold text-[#032352]">Expenses</h1>
          <p className="text-[#5B5C5F] mt-1">Track business expenses, purchases and fuel.</p>
        </div>
        <Button data-testid="add-expense-button" onClick={openNew} className="bg-[#032352] hover:bg-[#04347A]">
          <Plus className="w-4 h-4 mr-2" /> New Expense
        </Button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent data-testid="expense-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-[#032352]">
                {editingId ? "Edit Expense" : "New Expense"}
              </DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  data-testid="expense-date-input"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                />
              </div>
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="expense-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>For (Staff)</Label>
                <Select value={form.staff} onValueChange={(v) => setForm({ ...form, staff: v })}>
                  <SelectTrigger data-testid="expense-staff-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={JOINT}>Joint Expense</SelectItem>
                    {staff.map((m) => (
                      <SelectItem key={m.id} value={m.id}>
                        {m.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Supplier / Payee</Label>
                <Input
                  data-testid="expense-supplier-input"
                  value={form.supplier}
                  onChange={(e) => setForm({ ...form, supplier: e.target.value })}
                  placeholder="e.g. Shell, Screwfix"
                />
              </div>
              <div>
                <Label>Amount (excl VAT £)</Label>
                <Input
                  type="number"
                  step="0.01"
                  data-testid="expense-amount-input"
                  value={form.amount_excl}
                  onChange={(e) => {
                    const ex = e.target.value;
                    const vat = ex === "" ? "" : (parseFloat(ex) * 0.2).toFixed(2);
                    setForm({ ...form, amount_excl: ex, vat_amount: vat });
                  }}
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label>VAT (£)</Label>
                <Input
                  type="number"
                  step="0.01"
                  data-testid="expense-vat-input"
                  value={form.vat_amount}
                  onChange={(e) => setForm({ ...form, vat_amount: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div className="sm:col-span-2">
                <Label>Notes</Label>
                <Textarea
                  data-testid="expense-notes-input"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                />
              </div>
            </div>

            {/* P&L toggle */}
            <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 mt-2">
              <div>
                <p className="text-sm font-medium text-[#032352]">Include in Profit & Loss</p>
                <p className="text-xs text-[#5B5C5F] mt-0.5">Turn off for non-operational costs (e.g. owner drawings, personal expenses)</p>
              </div>
              <button
                type="button"
                data-testid="expense-include-pl-toggle"
                onClick={() => setForm({ ...form, include_in_pl: !form.include_in_pl })}
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
                  form.include_in_pl ? "bg-[#032352]" : "bg-slate-300"
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform duration-200 ${
                    form.include_in_pl ? "translate-x-5" : "translate-x-0"
                  }`}
                />
              </button>
            </div>

            <DialogFooter>
              <Button
                data-testid="save-expense-button"
                onClick={save}
                disabled={saving}
                className="bg-[#032352] hover:bg-[#04347A]"
              >
                Save Expense
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Insight header */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="font-heading font-semibold text-[#032352]">Insights</h2>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={preset} onValueChange={setPreset}>
              <SelectTrigger className="w-44 bg-white" data-testid="expenses-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRESETS.map((p) => (
                  <SelectItem key={p.value} value={p.value}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {preset === "custom" && (
              <>
                <Input
                  type="date"
                  data-testid="expenses-custom-start"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                  className="w-40 bg-white"
                />
                <Input
                  type="date"
                  data-testid="expenses-custom-end"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  className="w-40 bg-white"
                />
              </>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Stat label="Total Spent" value={currency(insight.total)} icon={PoundSterling} accent="#EF4444" testid="insight-total" sub="Incl VAT" />
          <Stat label="Net (Ex VAT)" value={currency(insight.ex)} icon={PoundSterling} accent="#032352" testid="insight-net" />
          <Stat label="VAT to Claim" value={currency(insight.vat)} icon={Percent} accent="#10B981" testid="insight-vat" sub="Reclaimable VAT" />
          <Stat label="Entries" value={insight.count} icon={Hash} accent="#5B5C5F" testid="insight-count" />
        </div>
      </div>

      <div className="relative w-full max-w-sm mb-4">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5B5C5F]" />
        <Input
          data-testid="expenses-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search expenses..."
          className="pl-9"
        />
      </div>

      <div className="bg-white rounded-md border border-slate-200 overflow-x-auto">
        <Table className="min-w-[820px]">
          <TableHeader>
            <TableRow className="bg-[#F8FAFC]">
              <TableHead>Date</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>For</TableHead>
              <TableHead>Supplier</TableHead>
              <TableHead className="text-right">Ex VAT</TableHead>
              <TableHead className="text-right">VAT</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead className="w-20"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-[#5B5C5F] py-10">
                  <Receipt className="w-6 h-6 mx-auto mb-2 opacity-50" />
                  No expenses found.
                </TableCell>
              </TableRow>
            )}
            {filtered.map((e) => (
              <TableRow key={e.id} data-testid={`expense-row-${e.id}`}
                className={e.include_in_pl === false ? "opacity-60" : ""}>
                <TableCell className="text-[#5B5C5F] text-sm">{fmtDate(e.date)}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1.5">
                    <Badge variant="secondary" className="font-normal">{e.category}</Badge>
                    {e.include_in_pl === false && (
                      <span className="text-[9px] uppercase tracking-wider font-semibold text-slate-400 border border-slate-200 rounded px-1 py-0.5 leading-none">
                        excl. P&L
                      </span>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-sm">
                  {e.joint || !e.staff_name ? (
                    <Badge variant="outline" className="font-normal text-[#5B5C5F]">Joint</Badge>
                  ) : (
                    <span className="text-[#032352]">{e.staff_name}</span>
                  )}
                </TableCell>
                <TableCell className="text-[#032352]">{e.supplier || "—"}</TableCell>
                <TableCell className="text-right font-mono-num text-[#5B5C5F]">
                  {currency(e.amount_excl)}
                </TableCell>
                <TableCell className="text-right font-mono-num text-[#5B5C5F]">
                  {currency(e.vat_amount)}
                </TableCell>
                <TableCell className="text-right font-mono-num text-red-600 font-medium">
                  {currency(e.total)}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-3 justify-end">
                    <button
                      data-testid={`edit-expense-${e.id}`}
                      onClick={() => openEdit(e)}
                      className="text-slate-400 hover:text-[#032352] transition-colors"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      data-testid={`delete-expense-${e.id}`}
                      onClick={() => remove(e.id, e.description || e.supplier || e.category)}
                      className="text-slate-400 hover:text-red-500 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <ConfirmDialog
        open={!!deleteTarget}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        loading={deleting}
        title="Delete Expense?"
        description={`This will permanently delete this expense${deleteTarget?.desc ? ` (${deleteTarget.desc})` : ""}. This cannot be undone.`}
      />
    </div>
  );
}
